import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, type ModalProps } from '@/shared/components/molecules/Modal';
import { type CastCacheValidatorFragment } from '@/shared/graphql/cache/fragments';
import useOldFundingSDK from '@/shared/hooks/useOldFundingSdk';
import { PENDING_REDEMPTION } from '@/shared/queryKeys';
import type TokenAmount from '@/shared/utils/TokenAmount';
import Step1 from './Step1';
import Step2 from './Step2';
import { POLL_INTERVAL } from '../../../../utils/consts';

interface RedeemFundsModalProps extends ModalProps {
  validator: Omit<CastCacheValidatorFragment, 'totalBalance'> & { totalBalance: TokenAmount };
}

const RedeemFundsModal = ({ active, onCancel, validator }: RedeemFundsModalProps) => {
  const { fundingSDK } = useOldFundingSDK();

  const { data: pendingRedemption, refetch } = useQuery({
    queryKey: [PENDING_REDEMPTION, validator.idHex, Boolean(fundingSDK)],
    queryFn: async () =>
      (await fundingSDK?.getPendingRedemption(validator.idHex as `0x${string}`)) ?? null,
    refetchInterval: POLL_INTERVAL,
  });

  const content = useMemo(() => {
    if (pendingRedemption) {
      return (
        <Step2
          validator={validator}
          pendingRedemption={pendingRedemption}
          onCancel={onCancel}
          onComplete={refetch}
        />
      );
    }

    return <Step1 validator={validator} />;
  }, [pendingRedemption]);

  return (
    <Modal
      active={active}
      headingJSX={
        <span className="text-14 text-cf-green-1">Step {!pendingRedemption ? 1 : 2}/2</span>
      }
      onCancel={onCancel}
    >
      <div className="flex flex-col">{content}</div>
    </Modal>
  );
};

export default RedeemFundsModal;
